import { useState } from "react";
import { FieldGrid } from "@/components/contracts/FieldGrid";
import { Badge } from "@/components/ui/badge";
import type { PrivateHire, PrivateHireMovement } from "@/lib/coach-manager/types";
import { PrivateHireDataValuesTable } from "./PrivateHireDataValuesTable";
import { PrivateHireMovementsTable } from "./PrivateHireMovementsTable";
import { privateHireStatusBadge } from "./PrivateHiresTable";

/**
 * Full read-only display of one `PrivateHire`: a full-width top strip (the
 * PrivateHire's own fields, incl. its invoicing/financial fields + its data
 * rows), then a full-width Movements table below. Mirrors `ContractDetail`
 * exactly — purpose-built, not a generic tree renderer, since the shape is
 * fixed and fully known from `src/lib/coach-manager/types.ts`. No action
 * buttons — display only.
 */
export function PrivateHireDetail({ privateHire }: { privateHire: PrivateHire }) {
  const movements = privateHire.PrivateHireMovements ?? [];
  const [expandedMovementId, setExpandedMovementId] = useState<number | null>(
    movements[0]?.PrivateHireMovementID ?? null,
  );

  const handleToggle = (movement: PrivateHireMovement) => {
    setExpandedMovementId((current) =>
      current === movement.PrivateHireMovementID ? null : movement.PrivateHireMovementID,
    );
  };

  const status = privateHireStatusBadge(privateHire);

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <h3 className="font-display text-[19px] font-bold">
            PrivateHire {privateHire.PrivateHireID}
          </h3>
          <Badge variant={status.variant}>{status.label}</Badge>
        </div>
        <FieldGrid
          fields={[
            { label: "Client ID", value: privateHire.ClientID },
            { label: "Booking Type", value: privateHire.BookingType },
            { label: "Company ID", value: privateHire.CompanyID },
            { label: "Base Serial No", value: privateHire.BaseSerialNo },
            { label: "Operator ID", value: privateHire.OperatorID },
            { label: "Date Created", value: privateHire.DateCreated },
            { label: "Hold Invoice", value: privateHire.HoldInvoice },
            { label: "Combined Invoice", value: privateHire.CombinedInvoice },
            { label: "Deposit Requested", value: privateHire.DepositRequested },
            { label: "Price", value: privateHire.PrivateHirePrice },
            { label: "VAT", value: privateHire.PrivateHireVAT },
            { label: "Total", value: privateHire.PrivateHireTotal },
            { label: "Invoiced Amount", value: privateHire.InvoicedAmount },
            { label: "Received Amount", value: privateHire.RecievedAmount },
            { label: "Outstanding Amount", value: privateHire.OutstandingAmount },
            { label: "Last Invoice No", value: privateHire.LastInvoiceNo },
            { label: "Last Invoiced Date", value: privateHire.LastInvoicedDate },
            { label: "Last Confirmation Date", value: privateHire.LastConfirmationDate },
            { label: "Terms Of Trade", value: privateHire.TermsOfTrade },
            {
              label: "Quotation ID",
              value: privateHire.QuotationID > 0 ? privateHire.QuotationID : undefined,
            },
          ]}
        />
        {privateHire.PrivateHireData && privateHire.PrivateHireData.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-display text-sm font-semibold">
              PrivateHire Data ({privateHire.PrivateHireData.length})
            </h4>
            <PrivateHireDataValuesTable rows={privateHire.PrivateHireData} />
          </div>
        )}
      </div>

      <div className="space-y-2">
        <h4 className="font-display text-sm font-semibold">Movements ({movements.length})</h4>
        <p className="text-xs text-muted-foreground">
          Select a movement to view its detail, vehicles, pickups, setdowns and extras.
        </p>
        <PrivateHireMovementsTable
          movements={movements}
          expandedMovementId={expandedMovementId}
          onToggle={handleToggle}
        />
      </div>
    </div>
  );
}
